import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '@/state/language/LanguageProvider';
import { ClientTypeSelectionStep } from '@/components/onboarding/ClientTypeSelectionStep';
import { InvitationCodeStep } from '@/components/onboarding/InvitationCodeStep';
import { IndustrySelectionStep } from '@/components/onboarding/IndustrySelectionStep';
import { OrganizationDetailsStep } from '@/components/onboarding/OrganizationDetailsStep';
import { TemplateSelectionStep } from '@/components/onboarding/TemplateSelectionStep';
import { CustomizationStep } from '@/components/onboarding/CustomizationStep';
import { ReviewStep } from '@/components/onboarding/ReviewStep';
import { SuccessStep } from '@/components/onboarding/SuccessStep';
import { onboardingService } from '@/services/onboardingService';

export interface OnboardingData {
  // Step 0: Client Type Selection
  clientType: 'INDIVIDUAL' | 'TENANT_ADMIN' | 'TENANT_USER' | 'CONSULTANT' | 'SUPER_TENANT_ADMIN' | 'PORTCO_ADMIN' | 'PORTCO_USER';
  
  // Step 1: Industry Selection (for TENANT_ADMIN, TENANT_USER, CONSULTANT, SUPER_TENANT_ADMIN, PORTCO_ADMIN, PORTCO_USER clients)
  industryType: string;
  
  // Invitation code for TENANT_USER, PORTCO_ADMIN, PORTCO_USER
  invitationCode?: string;
  
  // Super Tenant invitation code for PORTCO_ADMIN, PORTCO_USER
  superTenantInvitationCode?: string;
  
  // Step 2: Organization Details
  organizationSize: string;
  geographicScope: string;
  companyName: string;
  domain: string;
  primaryLocation: string;
  timeZone: string;
  language: string;
  
  // Step 3: Template Selection
  selectedRoleTemplates: string[];
  selectedUserTemplates: string[];
  selectedHierarchyTemplates: string[];
  
  // Step 4: Customization
  customizations: {
    roleCustomizations: Record<string, any>;
    userCustomizations: Record<string, any>;
    hierarchyCustomizations: Record<string, any>;
  };
  
  // Step 5: Review
  isReady: boolean;
}

const STEPS = [
  { id: 'clientType', title: 'Client Type Selection', component: ClientTypeSelectionStep },
  { id: 'invitationCode', title: 'Invitation Code', component: InvitationCodeStep },
  { id: 'industry', title: 'Industry Selection', component: IndustrySelectionStep },
  { id: 'organization', title: 'Organization Details', component: OrganizationDetailsStep },
  { id: 'templates', title: 'Template Selection', component: TemplateSelectionStep },
  { id: 'customization', title: 'Customization', component: CustomizationStep },
  { id: 'review', title: 'Review & Setup', component: ReviewStep },
  { id: 'success', title: 'Success', component: SuccessStep },
];

export const OnboardingWizard: React.FC = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [onboardingData, setOnboardingData] = useState<OnboardingData>({
    clientType: 'TENANT_ADMIN', // Default to tenant admin onboarding
    industryType: '',
    organizationSize: '',
    geographicScope: '',
    companyName: '',
    domain: '',
    primaryLocation: '',
    timeZone: '',
    language: 'en-US',
    selectedRoleTemplates: [],
    selectedUserTemplates: [],
    selectedHierarchyTemplates: [],
    customizations: {
      roleCustomizations: {},
      userCustomizations: {},
      hierarchyCustomizations: {},
    },
    isReady: false,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const currentStepData = STEPS[currentStep];
  const CurrentStepComponent = currentStepData.component;

  const handleNext = (stepData: Partial<OnboardingData>) => {
    setOnboardingData(prev => ({ ...prev, ...stepData }));
    
    // Handle different flows based on client type
    if (stepData.clientType === 'INDIVIDUAL') {
      // Individual users skip most steps - go directly to success
      if (currentStep === 0) {
        setCurrentStep(STEPS.length - 1); // Skip to success step
      } else if (currentStep < STEPS.length - 1) {
        setCurrentStep(prev => prev + 1);
      }
    } else if (stepData.clientType === 'TENANT_USER' || stepData.clientType === 'PORTCO_ADMIN' || stepData.clientType === 'PORTCO_USER') {
      // Tenant users and Portco users need invitation code validation
      if (currentStep === 0) {
        // Go to invitation code step (index 1)
        setCurrentStep(1);
      } else if (currentStep < STEPS.length - 1) {
        setCurrentStep(prev => prev + 1);
      }
    } else {
      // Regular flow for TENANT_ADMIN, CONSULTANT, and SUPER_TENANT_ADMIN clients
      if (currentStep < STEPS.length - 1) {
        setCurrentStep(prev => prev + 1);
      }
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleComplete = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const result = await onboardingService.setupTenantOnboarding({
        tenantId: 'current-tenant', // This would come from auth context
        industryType: onboardingData.industryType as any,
        organizationSize: onboardingData.organizationSize as any,
        geographicScope: onboardingData.geographicScope as any,
        customizations: {
          companyName: onboardingData.companyName,
          domain: onboardingData.domain,
          primaryLocation: onboardingData.primaryLocation,
          timeZone: onboardingData.timeZone,
          language: onboardingData.language,
        },
      });

      if (result.success) {
        setCurrentStep(STEPS.length - 1); // Move to success step
      } else {
        setError(result.error || 'Onboarding failed');
      }
    } catch (err: any) {
      setError(err.message || 'An error occurred during onboarding');
    } finally {
      setIsLoading(false);
    }
  };

  const handleFinish = () => {
    navigate('/dashboard');
  };

  const progress = ((currentStep + 1) / STEPS.length) * 100;

  return (
    <div className="min-h-screen bg-[var(--color-secondary-bg)]">
      {/* Header */}
      <div className="bg-[var(--color-surface-bg)] shadow-sm border-b border-[var(--color-border)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div>
              <h1 className="text-xl font-bold text-[var(--color-surface-text)]">
                {t('onboarding.title')}
              </h1>
              <p className="mt-1 text-xs text-[var(--color-secondary-text)]">
                {t('onboarding.subtitle')}
              </p>
            </div>
            <div className="text-xs text-[var(--color-secondary-text)]">
              {t('onboarding.step')} {currentStep + 1} {t('common.of')} {STEPS.length}
            </div>
          </div>
          
          {/* Progress Bar */}
          <div className="w-full bg-[var(--color-border)] rounded-full h-2">
            <div 
              className="bg-[var(--color-info)] h-2 rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-[var(--color-surface-bg)] rounded-lg shadow-sm border border-[var(--color-border)]">
          <div className="px-6 py-8">
            {error && (
              <div className="mb-6 bg-[var(--color-error)]/10 border border-[var(--color-error)]/20 rounded-md p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-[var(--color-error)]" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-[var(--color-error)]">
                      {t('onboarding.error.title')}
                    </h3>
                    <div className="mt-2 text-sm text-[var(--color-error)]">
                      {error}
                    </div>
                  </div>
                </div>
              </div>
            )}

            <CurrentStepComponent
              data={onboardingData}
              onNext={handleNext}
              onPrevious={handlePrevious}
              onComplete={handleComplete}
              onFinish={handleFinish}
              isLoading={isLoading}
              error={error}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
